import React, { FormEvent, MutableRefObject, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useVisibilityObserver } from '@noths/polaris-client-react-hooks';
import { isBreakpointSmallOrBelow, useBreakpoint } from '@noths/polaris-client-ribbons-base';
import {
  NotificationInfo,
  NotificationWarning,
  Price,
} from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';
import { UserConfigurationState } from '@noths/polaris-client-user-configuration';
import { DeliveryByEvent, deliveryByEventNames } from '@noths/polaris-dev-ts-types';

import { AnchorToSection } from 'src/components/atoms/AnchorToSection';
import { Quantity } from 'src/components/atoms/Quantity';
import { DeliveryBlockContainer } from 'src/components/molecules/DeliveryBlock';
import { DeliveryEventContainer } from 'src/components/molecules/DeliveryEvent';
import { PriceBreakdownContainer } from 'src/components/molecules/PriceBreakdown';
import { StarRating } from 'src/components/molecules/StarRating/components/StarRating';
import { CrossSellModalContainer } from 'src/components/organisms/CrossSellModal';
import { PersonalisationContainer } from 'src/components/organisms/Personalisation';
import {
  InteractionErrorTrackingEvent,
  PRODUCT_PERSONALISATIONS_VISIBILITY_NAME,
  TrackedEvents,
} from 'src/redux/personalisation';
import { FavouriteProduct } from 'src/redux/selectors';
import { sectionVisible } from 'src/redux/visibilityTracking';
import { Basket, BasketItem, ProductOption } from 'src/types';
import { isMadeToOrder, isOutOfStock, isSuspended, removeHtmlElementFromString } from 'src/utils';
import { NEW_PRODUCT_LABEL, PRODUCT_REVIEWS_ID } from '../constants';
import { NO_LONGER_AVAILABLE_LABEL, OUT_OF_STOCK_LABEL } from '../constants/copy';
import { AddToBasketButtonContainer } from '../containers/AddToBasketContainer';
import { KlarnaContainer } from '../containers/KlarnaContainer';
import { DELIVERY_SUMMARY_VISIBILITY_NAME } from '../tracking/events';
import { CartModal } from './CartModal';
import { ErrorNotification } from './ErrorNotification';
import { MadeToOrder } from './MadeToOrder';
import * as styles from '../styles/ProductInfo.styles';

export const PRICE = 'price';
export const PREVIOUS_PRICE = 'previous-price';

export interface ProductInfoProps {
  basket: Basket | null;
  deliveryByEvent: DeliveryByEvent | null;
  editItem: BasketItem | undefined;
  hasAddToBasketErrors: boolean;
  hideAddToCartPlacement: boolean;
  isBasketRequestPending: boolean;
  isCartModalOpen: boolean;
  isPersonalisationOptionsExpanded: boolean;
  outstandingSelections: ProductOption[];
  product: FavouriteProduct;
  richRelevanceSessionId?: string;
  shouldShowPersonalisationValidationErrors: boolean;
  trackedEvents: TrackedEvents;
  userConfiguration: UserConfigurationState;
  addToBasket: VoidFunction;
  changeQuantity: (payload: { quantity: number; isValid: boolean }) => void;
  onCartGoToBasket: () => void;
  onCartModalClose: VoidFunction;
  onCrossSellModalClose: VoidFunction;
  onProductReviewsReadReviews: () => void;
  openCartModal: VoidFunction;
  submitPersonalisationForm: () => void;
  toggleExpanded: VoidFunction;
  trackErrorInteraction: (payload: InteractionErrorTrackingEvent) => void;
}

const handleQtyChange = (
  value: string,
  isValid: boolean,
  changeQuantity: ProductInfoProps['changeQuantity'],
) => {
  changeQuantity({ quantity: Number(value), isValid });
};

const handlePersonalisationFormSubmit = (
  e: FormEvent,
  submitPersonalisationForm: ProductInfoProps['submitPersonalisationForm'],
) => {
  e.preventDefault();

  submitPersonalisationForm();
};

export const ProductInfo = ({
  basket,
  deliveryByEvent,
  editItem,
  hasAddToBasketErrors,
  hideAddToCartPlacement,
  isBasketRequestPending,
  isCartModalOpen,
  isPersonalisationOptionsExpanded,
  product,
  richRelevanceSessionId,
  shouldShowPersonalisationValidationErrors,
  userConfiguration,
  changeQuantity,
  onCartGoToBasket,
  onCartModalClose,
  onCrossSellModalClose,
  onProductReviewsReadReviews,
  submitPersonalisationForm,
  toggleExpanded,
}: ProductInfoProps) => {
  const dispatch = useDispatch();
  const { observedElementRef: productPersonalisationsRef } = useVisibilityObserver(() =>
    dispatch(sectionVisible({ sectionName: PRODUCT_PERSONALISATIONS_VISIBILITY_NAME })),
  );
  const { observedElementRef: deliverySummaryRef } = useVisibilityObserver(() =>
    dispatch(sectionVisible({ sectionName: DELIVERY_SUMMARY_VISIBILITY_NAME })),
  );
  const formRef: MutableRefObject<HTMLFormElement | null> = useRef(null);

  const breakpoint = useBreakpoint();
  const isSmallScreen = isBreakpointSmallOrBelow(breakpoint);
  const isUKAndGBP =
    userConfiguration.countryCode === 'GB' && userConfiguration.currencyCode === 'GBP';
  const targetingAdvertisingCookiesConsented =
    userConfiguration.cookieConsent?.targetingAdvertisingCookiesConsented;
  const preSalePrice = product.preSalePrices?.find(
    (price) => price.currency === product.price.currency,
  );
  const renderInventoryStatus = () => {
    switch (true) {
      case isMadeToOrder(product.inventoryStatus):
        return <MadeToOrder />;
      case isSuspended(product.availability, product.available):
        return <NotificationWarning>{NO_LONGER_AVAILABLE_LABEL}</NotificationWarning>;
      case isOutOfStock(product.inventoryStatus):
        return <NotificationInfo>{OUT_OF_STOCK_LABEL}</NotificationInfo>;
      default:
        return null;
    }
  };

  return (
    <div css={styles.productInfoWrapper}>
      <ErrorNotification returnFocusToRef={formRef} shouldShow={hasAddToBasketErrors} />
      <h1 css={styles.heading}>{product.title}</h1>
      <a
        css={styles.link}
        href={`/${product.partner.shortcode}`}
        aria-label={`by ${product.partner.name}`}
      >
        {product.partner.name}
      </a>
      <div css={styles.prices}>
        <div>
          <Price
            testId={PRICE}
            isSalePrice={Boolean(product.onSale && preSalePrice)}
            css={styles.styledPrice}
            {...product.price}
          />
          {product.onSale && preSalePrice && (
            <Price
              testId={PREVIOUS_PRICE}
              isPreviousPrice={true}
              css={styles.previousPrice}
              {...preSalePrice}
            />
          )}
        </div>
      </div>
      {renderInventoryStatus()}
      {deliveryByEvent?.arriveByEvent &&
        deliveryByEventNames.includes(deliveryByEvent?.eventName) && <DeliveryEventContainer />}
      {targetingAdvertisingCookiesConsented && isUKAndGBP && <KlarnaContainer />}
      <hr css={styles.divider} />
      {product.productRating && (
        <AnchorToSection
          css={styles.productRating}
          elementId={PRODUCT_REVIEWS_ID}
          onClick={onProductReviewsReadReviews}
        >
          <StarRating
            productRating={product.productRating}
            numberOfReviews={product.numberOfReviews}
            accessibleLabel={`Overall rating ${product.productRating} out of 5`}
          />
        </AnchorToSection>
      )}
      {product.isNew && <p css={visuallyHidden}>{NEW_PRODUCT_LABEL}</p>}
      <p
        dangerouslySetInnerHTML={{ __html: removeHtmlElementFromString(product.description, 'p') }}
      />
      <form
        onSubmit={(e) => handlePersonalisationFormSubmit(e, submitPersonalisationForm)}
        noValidate
        ref={formRef}
      >
        {product.purchasable && (
          <div ref={productPersonalisationsRef}>
            <PersonalisationContainer
              toggleExpanded={toggleExpanded}
              displayValidation={shouldShowPersonalisationValidationErrors}
              isExpanded={isPersonalisationOptionsExpanded}
            />
          </div>
        )}
        {!isOutOfStock(product.inventoryStatus) && (
          <>
            {Boolean(product.options.length) && <PriceBreakdownContainer />}
            <Quantity
              onChange={(value, valid) => handleQtyChange(value, valid, changeQuantity)}
              defaultValue={editItem?.quantity || product.quantity}
            />
          </>
        )}
        <AddToBasketButtonContainer preventAddToBasketSubmit={true} />
      </form>
      {!isOutOfStock(product.inventoryStatus) && (
        <div ref={deliverySummaryRef}>
          <DeliveryBlockContainer css={styles.deliveryBlock} />
        </div>
      )}
      {isSmallScreen && <CrossSellModalContainer onModalClose={onCrossSellModalClose} />}
      <CartModal
        isModalOpen={isCartModalOpen && !isBasketRequestPending && !hasAddToBasketErrors}
        basket={basket}
        product={product}
        onModalClose={onCartModalClose}
        onModalApply={onCartGoToBasket}
        hideAddToCartPlacement={hideAddToCartPlacement}
        richRelevanceSessionId={richRelevanceSessionId}
      />
    </div>
  );
};
